import React, { useCallback, useEffect, useState } from 'react';
import { useUser } from '../context';
import { ProviderLogo } from '../ProviderLogos';

const providerLabel = (provider: string): string => {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'microsoft':
      return 'Microsoft';
    default:
      return provider;
  }
};

const inputClassName =
  'mt-1 block w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-base font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60';

const AccountProfileSection = () => {
  const { user, refreshUser } = useUser();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
  }, [user]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!user) return;

      setSaving(true);
      setError(null);
      setSaved(false);

      try {
        const res = await fetch('/api/user/profile', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firstName, lastName }),
        });

        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(data?.error ?? `Save failed (${res.status})`);
        }

        await refreshUser();
        setSaved(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    },
    [firstName, lastName, refreshUser, user],
  );

  if (!user) return null;

  const savedFirstName = user.firstName ?? '';
  const savedLastName = user.lastName ?? '';
  const hasChanges = firstName !== savedFirstName || lastName !== savedLastName;

  return (
    <section
      className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
      aria-labelledby="profile-section-heading"
    >
      <h2
        id="profile-section-heading"
        className="font-display text-xl font-semibold text-ink"
      >
        Profile
      </h2>
      <p className="mt-1 font-sans text-sm text-ink/65">
        Update your name and review your sign-in details.
      </p>

      <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
        <div className="grid gap-5 sm:grid-cols-2">
          <label className="block">
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              First name
            </span>
            <input
              type="text"
              name="firstName"
              value={firstName}
              onChange={(event) => {
                setFirstName(event.target.value);
                setSaved(false);
              }}
              autoComplete="given-name"
              className={inputClassName}
            />
          </label>
          <label className="block">
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              Last name
            </span>
            <input
              type="text"
              name="lastName"
              value={lastName}
              onChange={(event) => {
                setLastName(event.target.value);
                setSaved(false);
              }}
              autoComplete="family-name"
              className={inputClassName}
            />
          </label>
        </div>

        <div className="rounded-2xl border-2 border-ink/10 bg-paper/50 p-4">
          <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
            Email
          </p>
          <p className="mt-1 font-sans text-base font-semibold text-ink">{user.email}</p>
        </div>

        <div className="rounded-2xl border-2 border-ink/10 bg-paper/50 p-4">
          <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
            Sign-in provider
          </p>
          <div className="mt-2 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-ink/10 bg-white shadow-sm">
              <ProviderLogo provider={user.provider} className="h-5 w-5" />
            </span>
            <span className="font-sans text-base font-semibold text-ink">
              {providerLabel(user.provider)}
            </span>
          </div>
        </div>

        {error ? (
          <p className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink" role="alert">
            {error}
          </p>
        ) : null}

        {saved ? (
          <p className="rounded-xl bg-grass/30 px-3 py-2 font-sans text-sm font-semibold text-ink" role="status">
            Profile saved.
          </p>
        ) : null}

        <button
          type="submit"
          disabled={saving || !hasChanges}
          className="rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/35 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save profile'}
        </button>
      </form>
    </section>
  );
};

export default AccountProfileSection;
